import {
    useInfiniteQuery,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query';

import {
    deleteRecord,
    fetchRecords,
} from '../services/SubscriptionItemsService';
import {
    notifyError,
    notifySuccess,
} from '../../../components/ServiceNotifications';

// FNP-918: the data layer for the subscription-items list.
//
// Query keys are STRUCTURED and module-scoped (checklist §8). The previous key
// was `['subscriptionItems', customerId, currentToken]` — a bare string plus a
// page token, which makes the cache page-shaped rather than list-shaped.
//
// ⚠️ This module is SUBSCRIPTION-SCOPED. The old key called its scope
// `customerId` while being handed a subscription id; see the service.
export const subscriptionItemsKeys = {
    all: ['subscription-items'] as const,
    lists: () => [...subscriptionItemsKeys.all, 'list'] as const,
    list: (subscriptionId: string, filters: Record<string, string> = {}) =>
        [...subscriptionItemsKeys.lists(), subscriptionId, {filters}] as const,
    details: () => [...subscriptionItemsKeys.all, 'detail'] as const,
    detail: (id: string) => [...subscriptionItemsKeys.details(), id] as const,
};

// Page size is a SERVER concern under token pagination — it is the `limit` sent
// with the query, fixed by the module. There is deliberately no rows-per-page
// dropdown: there is no page to resize and no total to divide (checklist §9).
const PAGE_SIZE = 50;

/**
 * FNP-918: TOKEN PAGINATION.
 *
 * This was `useQuery` + the app-local `usePagination` token stack, which reads
 * as compliant while being a page-at-a-time model the shared footer cannot
 * describe. `useInfiniteQuery` concatenates pages instead.
 *
 * ⚠️ `getNextPageParam` MUST return `undefined` — not `null`, and never the
 * same token back — when there are no more pages. Returning `null` leaves
 * `hasNextPage` true and react-query refetches forever.
 */
export const useSubscriptionItemsInfiniteQuery = (
    subscriptionId: string,
    filters: Record<string, string> = {},
) => {
    return useInfiniteQuery({
        queryKey: subscriptionItemsKeys.list(subscriptionId, filters),
        queryFn: ({pageParam}: {pageParam: string | null}) =>
            fetchRecords(subscriptionId, PAGE_SIZE, pageParam ?? undefined),
        getNextPageParam: (lastPage) => lastPage?.nextToken ?? undefined,
        // Explicit annotation, or `pageParam` type-errors (§9).
        initialPageParam: null as string | null,
        // The API REQUIRES a subscription in the filter, so an empty id would
        // be a guaranteed error rather than an empty list.
        enabled: !!subscriptionId,
    });
};

/**
 * Batch delete. The whole module key is invalidated on success so the table
 * reflects the removal without a manual reload (§8).
 */
export const useDeleteSubscriptionItemsMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (ids: string[]) =>
            Promise.all(ids.map(id => deleteRecord(id))),
        onSuccess: (_data, ids) => {
            notifySuccess(
                ids.length === 1
                    ? 'Subscription item deleted'
                    : `${ids.length} subscription items deleted`,
            );
            queryClient.invalidateQueries({queryKey: subscriptionItemsKeys.all});
        },
        onError: notifyError,
    });
};
